import {
    ConditionalFormattingColorApplyTo,
    FieldType,
    formatItemValue,
    getConditionalFormattingColor,
    getConditionalFormattingConfig,
    getConditionalFormattingDescription,
    getItemId,
    isDimension,
    isField,
    isHexCodeColor,
    isNumericItem,
    type ColumnProperties,
    type ConditionalFormattingConfig,
    type ConditionalFormattingMinMaxMap,
    type ConditionalFormattingRowFields,
    type ItemsMap,
    type ParametersValuesMap,
    type PivotData,
    type ResultRow,
    type ResultValue,
    type SortField,
} from '@lightdash/common';
import { Menu } from '@mantine-8/core';
import {
    Box,
    Button,
    Group,
    Text,
    useMantineColorScheme,
    type BoxProps,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import {
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
    type ExpandedState,
    type GroupingState,
    type Row,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';
import { readableColor } from 'polished';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
} from 'react';
import {
    findMatchingSubtotal,
    getGroupingValuesAndSubtotalKey,
    getSubtotalValueFromGroup,
} from '../../../hooks/tableVisualization/getDataAndColumns';
import {
    formatCellContent,
    getFormattedValueCell,
} from '../../../hooks/useColumns';
import {
    getColorFromRange,
    transformColorsForDarkMode,
} from '../../../utils/colorUtils';
import {
    getMetricLabelHeaderRowIndex,
    getPivotColumnIdentities,
} from '../../../utils/pivotColumnIdentity';
import { getSortIcon } from '../../../utils/sortUtils';
import { getConditionalRuleLabelFromItem } from '../Filters/FilterInputs/utils';
import Table from '../LightTable';
import { CELL_HEIGHT } from '../LightTable/constants';
import MantineIcon from '../MantineIcon';
import { ROW_NUMBER_COLUMN_ID } from '../Table/constants';
import { getGroupedRowModelLightdash } from '../Table/getGroupedRowModelLightdash';
import { columnHelper, type TableColumn } from '../Table/types';
import { useColumnResize } from '../Table/useColumnResize';
import { countSubRows } from '../Table/utils';
import {
    getFrozenColumnLayout,
    type FrozenColumnEntry,
} from './getFrozenColumnLayout';
import { getGroupedDimColumnIds, getRowSpanMerges } from './getRowSpanMerges';
import pivotStyles from './PivotTable.module.css';
import TotalCellMenu from './TotalCellMenu';
import ValueCellMenu from './ValueCellMenu';

const MIN_AUTO_COL_WIDTH = 50;

const getStickyCellProps = (frozen: FrozenColumnEntry | undefined) => {
    if (!frozen) return {};
    return {
        className: `${pivotStyles.stickyColumn}${
            frozen.isLast ? ` ${pivotStyles.stickyColumnLast}` : ''
        }`,
        style: { left: frozen.left },
    };
};

const getStickyHeaderProps = (frozen: FrozenColumnEntry | undefined) => {
    if (!frozen) return {};
    return {
        className: `${pivotStyles.stickyColumn} ${pivotStyles.stickyHeaderColumn}${
            frozen.isLast ? ` ${pivotStyles.stickyColumnLast}` : ''
        }`,
        style: { left: frozen.left },
    };
};

type MenuCallbackProps = {
    isOpen: boolean;
    onClose: () => void;
    onCopy: () => void;
};

type RenderCallback = () => React.ReactNode;

const rowColumn: TableColumn = {
    id: ROW_NUMBER_COLUMN_ID,
    cell: (props) => props.row.index + 1,
    enableGrouping: false,
};

const allPivotedSpacerColumn: TableColumn = {
    id: 'all-pivoted-spacer',
    cell: () => null,
    enableGrouping: false,
};

const VirtualizedArea: FC<{
    cellCount: number;
    height: number;
}> = ({ cellCount, height }) => {
    return (
        <Table.Row index={-1}>
            {[...Array(cellCount)].map((_, index) => (
                <Table.Cell key={index} h={height} />
            ))}
        </Table.Row>
    );
};

type IndexDimension = { fieldId: string; value: unknown };

const extractIndexDimensions = (
    indexValueRow: PivotData['indexValues'][number],
): IndexDimension[] =>
    indexValueRow
        .filter((v) => v.type === 'value')
        .map((v) => ({
            fieldId: v.fieldId,
            value: 'value' in v ? v.value?.raw : undefined,
        }));

type PivotTableProps = BoxProps & // TODO: remove this
    React.RefAttributes<HTMLTableElement> & {
        data: PivotData;
        conditionalFormattings: ConditionalFormattingConfig[];
        minMaxMap: ConditionalFormattingMinMaxMap | undefined;
        hideRowNumbers: boolean;
        getFieldLabel: (fieldId: string) => string | undefined;
        getField: (fieldId: string) => ItemsMap[string] | undefined;
        showSubtotals?: boolean;
        showSubtotalsExpanded?: boolean;
        /**
         * When true, visually deduplicate repeated row-index dim values
         * without rendering aggregate subtotal rows. Implicitly true when
         * `showSubtotals` is true (subtotals always group).
         */
        showRowGrouping?: boolean;
        columnProperties?: Record<string, ColumnProperties>;
        isMinimal: boolean;
        isDashboard?: boolean;
        onColumnWidthChange?: (fieldId: string, width: number) => void;
        parameters?: ParametersValuesMap;
        sortBy?: SortField[];
        /** Renders inside a Mantine Menu opened by clicking sortable headers. */
        renderSortMenu?: (target: PivotSortMenuTarget) => React.ReactNode;
    };

export type PivotSortMenuTarget =
    | {
          kind: 'pivotColumn';
          dataColIndex: number;
          metricReference: string;
          pivotValues: Array<{ reference: string; value: unknown }>;
      }
    | { kind: 'indexDim'; reference: string }
    | { kind: 'groupByDim'; reference: string };

const PivotTable: FC<PivotTableProps> = ({
    data,
    conditionalFormattings,
    minMaxMap = {},
    hideRowNumbers = false,
    getFieldLabel,
    getField,
    className,
    showSubtotals = false,
    showSubtotalsExpanded = false,
    showRowGrouping = false,
    columnProperties = {},
    isMinimal = false,
    isDashboard = false,
    onColumnWidthChange,
    parameters,
    sortBy,
    renderSortMenu,
    ...tableProps
}) => {
    const { colorScheme } = useMantineColorScheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const [grouping, setGrouping] = React.useState<GroupingState>([]);
    // Row grouping without subtotals must always render expanded — there's
    // no UI affordance to expand groups in that mode (no carat) and
    // collapsed groups would hide all leaf data. Force-expanded when
    // showRowGrouping is on without subtotals; otherwise honor showSubtotalsExpanded.
    const groupingOnlyMode = showRowGrouping && !showSubtotals;
    const expandAllByDefault = showSubtotalsExpanded || groupingOnlyMode;
    const [expanded, setExpanded] = React.useState<ExpandedState>(
        expandAllByDefault ? true : {},
    );
    // Re-derive `expanded` when the "should expand all" predicate flips
    // (e.g. user toggles row grouping or subtotals-expanded). React's
    // recommended render-time setState pattern for derived state from
    // props — not a useEffect, no extra render (React detects the
    // setState before commit and just renders once with the new value).
    // We rely on `autoResetExpanded: false` (table config below) so
    // TanStack doesn't wipe this on setGrouping calls.
    const [prevExpandAllByDefault, setPrevExpandAllByDefault] =
        React.useState(expandAllByDefault);
    if (expandAllByDefault !== prevExpandAllByDefault) {
        setPrevExpandAllByDefault(expandAllByDefault);
        setExpanded(expandAllByDefault ? true : {});
    }

    const { handleResizeStart, resizeHandleClassName } = useColumnResize({
        onColumnWidthChange,
    });

    // Track actual rendered widths of body cells by column id, used to keep
    // frozen-column offsets aligned with reality. Without measurement, the
    // layout has to guess (defaultColumnWidth) and offsets visibly drift
    // when auto-sized cells differ from the guess.
    const [measuredColumnWidths, setMeasuredColumnWidths] = useState<
        Map<string, number>
    >(() => new Map());
    const cellObserversRef = useRef<Map<string, ResizeObserver>>(new Map());

    useEffect(
        () => () => {
            cellObserversRef.current.forEach((obs) => obs.disconnect());
            cellObserversRef.current.clear();
        },
        [],
    );

    const measureCellRef = useCallback(
        (columnId: string) => (el: HTMLTableCellElement | null) => {
            const existing = cellObserversRef.current.get(columnId);
            if (existing) {
                existing.disconnect();
                cellObserversRef.current.delete(columnId);
            }
            if (!el) return;
            const obs = new ResizeObserver(() => {
                const width = el.getBoundingClientRect().width;
                if (width === 0) return;
                setMeasuredColumnWidths((prev) => {
                    const current = prev.get(columnId);
                    if (
                        current !== undefined &&
                        Math.abs(current - width) < 0.5
                    ) {
                        return prev;
                    }
                    const next = new Map(prev);
                    next.set(columnId, width);
                    return next;
                });
            });
            obs.observe(el);
            cellObserversRef.current.set(columnId, obs);
        },
        [],
    );

    // Count label/index columns to compute offset from headerColIndex to pivotColumnInfo
    const numLabelCols = useMemo(
        () =>
            data.retrofitData.pivotColumnInfo.filter(
                (col) =>
                    col.columnType === 'label' ||
                    col.columnType === 'indexValue',
            ).length,
        [data],
    );

    const metricLabelHeaderRowIndex = useMemo(
        () => getMetricLabelHeaderRowIndex(data),
        [data],
    );
    const pivotColumnIdentities = useMemo(
        () => getPivotColumnIdentities(data),
        [data],
    );

    // Data-column index → matching sort entry. Built once so header render is O(1).
    const sortMatchByDataColIndex = useMemo(() => {
        const result = new Map<number, SortField>();
        if (!sortBy?.length) return result;

        for (const sort of sortBy) {
            const pivots = sort.pivotValues ?? [];

            if (pivots.length > 0) {
                // Pinned: pivot values must match, plus metric (when known)
                // — distinguishes per-metric sorts under the same pin.
                for (
                    let colIdx = 0;
                    colIdx < pivotColumnIdentities.length;
                    colIdx += 1
                ) {
                    const identity = pivotColumnIdentities[colIdx];
                    const allMatch = pivots.every((pv) => {
                        const found = identity.pivotValues.find(
                            (p) => p.reference === pv.reference,
                        );
                        return (
                            found !== undefined && found.rawValue === pv.value
                        );
                    });
                    if (!allMatch) continue;
                    const colMetric = identity.metricFieldId;
                    const metricMatches =
                        colMetric === undefined || colMetric === sort.fieldId;
                    if (metricMatches && !result.has(colIdx)) {
                        result.set(colIdx, sort);
                        break;
                    }
                }
                continue;
            }

            // Unpinned: surface on leftmost matching column, or col 0 when
            // there's no metric-label row (metricsAsRows: true).
            const item = getField?.(sort.fieldId);
            const isValueColumnSort =
                item && isField(item) && item.fieldType === FieldType.METRIC;
            if (!isValueColumnSort) continue;

            if (metricLabelHeaderRowIndex < 0) {
                if (pivotColumnIdentities.length > 0 && !result.has(0)) {
                    result.set(0, sort);
                }
                continue;
            }

            for (
                let colIdx = 0;
                colIdx < pivotColumnIdentities.length;
                colIdx += 1
            ) {
                if (
                    pivotColumnIdentities[colIdx].metricFieldId ===
                        sort.fieldId &&
                    !result.has(colIdx)
                ) {
                    result.set(colIdx, sort);
                    break;
                }
            }
        }
        return result;
    }, [getField, sortBy, pivotColumnIdentities, metricLabelHeaderRowIndex]);

    const hasColumnTotals = data.pivotConfig.columnTotals;

    const hasRowTotals = data.pivotConfig.rowTotals;

    const hasCustomWidths = useMemo(
        () =>
            Object.values(columnProperties).some(
                (prop) => prop?.width !== undefined,
            ),
        [columnProperties],
    );

    // Width of the leftmost row-number column. Mirrors the formula used by
    // the non-pivoted Table (see Table/TableProvider.tsx) so freeze offsets
    // line up with the actual rendered column width.
    const rowNumberWidth = useMemo(() => {
        if (hideRowNumbers) return 0;
        const rowCount = data.retrofitData.allCombinedData.length;
        return `${rowCount}`.length * 10 + 20;
    }, [hideRowNumbers, data.retrofitData.allCombinedData.length]);

    // In metricsAsRows mode the leftmost label column shows metric names
    // (Total order amount / Total completed order amount). Its synthetic
    // fieldId in pivotColumnInfo isn't a key in columnProperties — the
    // freeze toggle writes to the underlying metric fieldId instead. Map
    // the metric flags to a single "is the label column frozen?" signal.
    const isLabelColumnFrozen = useMemo(() => {
        if (!data.pivotConfig.metricsAsRows) return false;
        const labelMetricIds = new Set<string>();
        for (const row of data.indexValues) {
            for (const entry of row) {
                if (entry.type === 'label') labelMetricIds.add(entry.fieldId);
            }
        }
        for (const id of labelMetricIds) {
            if (columnProperties[id]?.frozen === true) return true;
        }
        return false;
    }, [data.indexValues, data.pivotConfig.metricsAsRows, columnProperties]);

    const frozenLayout = useMemo(
        () =>
            getFrozenColumnLayout({
                pivotColumnInfo: data.retrofitData.pivotColumnInfo,
                columnProperties,
                rowNumberWidth,
                defaultColumnWidth: 100,
                measuredWidths: measuredColumnWidths,
                labelColumnFrozen: isLabelColumnFrozen,
            }),
        [
            data.retrofitData.pivotColumnInfo,
            columnProperties,
            rowNumberWidth,
            measuredColumnWidths,
            isLabelColumnFrozen,
        ],
    );

    const { columns, columnOrder, colWidths } = useMemo(() => {
        // Pivoting all dimensions requires a spacer column under the pivoted headers.
        const allDimensionsPivoted =
            data.indexValueTypes.length === 0 &&
            data.titleFields[0].length === 1;

        const indexPlaceholders: Record<string, ResultValue>[] = Array(
            data.indexValueTypes.length,
        ).fill({});

        const headerPlaceholders: Record<string, ResultValue>[] = Array(
            data.headerValues.length,
        ).fill({});

        const headerInfoForColumns = data.headerValues.reduce<
            Array<Record<string, ResultValue>>
        >((acc, headerRow) => {
            return headerRow.map((headerColValue, headerColIndex) =>
                'value' in headerColValue
                    ? {
                          ...acc[headerColIndex],
                          [headerColValue.fieldId]: headerColValue.value,
                      }
                    : acc[headerColIndex],
            );
        }, headerPlaceholders);

        const finalHeaderInfoForColumns = [
            ...indexPlaceholders,
            ...headerInfoForColumns,
        ];

        const newColumnOrder: string[] = [];
        const newColWidths: (number | undefined)[] = [];
        if (!hideRowNumbers) {
            newColumnOrder.push(ROW_NUMBER_COLUMN_ID);
            newColWidths.push(rowNumberWidth);
        }
        if (allDimensionsPivoted) {
            newColumnOrder.push(allPivotedSpacerColumn.id);
            newColWidths.push(undefined);
        }

        let newColumns = data.retrofitData.pivotColumnInfo.map(
            (col, colIndex) => {
                newColumnOrder.push(col.fieldId);

                const itemId = col.underlyingId || col.baseId || col.fieldId;
                const item = itemId ? getField(itemId) : undefined;
                // For indexValue columns, use fieldId (actual dimension ID).
                // For data columns, use underlyingId (metric) or baseId (last pivot dim).
                const widthKey =
                    col.columnType === 'indexValue' ||
                    col.columnType === 'label'
                        ? col.fieldId
                        : col.underlyingId || col.baseId;
                const colWidth = widthKey
                    ? columnProperties[widthKey]?.width
                    : undefined;
                newColWidths.push(colWidth);
                const column: TableColumn = columnHelper.accessor(
                    (row: ResultRow) => {
                        return row[col.fieldId];
                    },
                    {
                        id: col.fieldId,
                        cell: getFormattedValueCell,
                        meta: {
                            item: item,
                            width: colWidth,
                            type: col.columnType,
                            headerInfo:
                                colIndex < finalHeaderInfoForColumns.length
                                    ? finalHeaderInfoForColumns[colIndex]
                                    : undefined,
                            frozenLayout: frozenLayout.get(col.fieldId),
                        },
                        aggregatedCell: (info) => {
                            if (info.row.getIsGrouped()) {
                                const groupingValuesAndSubtotalKey =
                                    getGroupingValuesAndSubtotalKey(info);

                                if (!groupingValuesAndSubtotalKey) {
                                    return null;
                                }

                                const { groupingValues, subtotalGroupKey } =
                                    groupingValuesAndSubtotalKey;

                                // Get the pivoted header values for the column
                                const pivotedHeaderValues =
                                    finalHeaderInfoForColumns[colIndex];

                                // Find the subtotal for the row, this is used to find the subtotal in the groupedSubtotals object
                                const subtotal = findMatchingSubtotal(
                                    data.groupedSubtotals?.[subtotalGroupKey],
                                    groupingValues,
                                    pivotedHeaderValues,
                                );

                                const subtotalValue = getSubtotalValueFromGroup(
                                    subtotal,
                                    col.baseId ?? col.fieldId,
                                );

                                if (subtotalValue === null) {
                                    return null;
                                }

                                return (
                                    <Text span fw={600}>
                                        {formatItemValue(
                                            item,
                                            subtotalValue,
                                            false,
                                            parameters,
                                        )}
                                    </Text>
                                );
                            }
                        },
                    },
                );
                return column;
            },
        );

        if (allDimensionsPivoted)
            newColumns = [allPivotedSpacerColumn, ...newColumns];
        if (!hideRowNumbers) newColumns = [rowColumn, ...newColumns];

        return {
            columns: newColumns,
            columnOrder: newColumnOrder,
            colWidths: newColWidths,
        };
    }, [
        data,
        hideRowNumbers,
        getField,
        columnProperties,
        frozenLayout,
        rowNumberWidth,
        parameters,
    ]);

    // Minimum table width so auto columns don't get squeezed to zero
    const minTableWidth = useMemo(() => {
        if (!hasCustomWidths) return undefined;
        return colWidths.reduce<number>(
            (sum, w) => sum + (w ?? MIN_AUTO_COL_WIDTH),
            0,
        );
    }, [hasCustomWidths, colWidths]);

    // Passthrough columns are registered with TanStack so their values
    // surface in `getAllCells()` for richText / image row context, but they
    // must not render as headers or body cells.
    const columnVisibility = useMemo(() => {
        const visibility: Record<string, boolean> = {};
        for (const col of data.retrofitData.pivotColumnInfo) {
            if (col.columnType === 'passthrough') {
                visibility[col.fieldId] = false;
            }
        }
        return visibility;
    }, [data.retrofitData.pivotColumnInfo]);

    const table = useReactTable({
        data: data.retrofitData.allCombinedData,
        columns: columns,
        state: {
            grouping,
            expanded,
            columnOrder: columnOrder,
            columnVisibility,
            columnPinning: {
                left: [ROW_NUMBER_COLUMN_ID],
            },
        },
        onGroupingChange: setGrouping,
        onExpandedChange: setExpanded,
        // Don't let TanStack auto-collapse all rows when grouping changes —
        // the expanded state is driven by `expandAllByDefault` (above) and
        // we want it to survive the setGrouping call. Without this,
        // toggling row-grouping leaves us with only top-level aggregate
        // rows visible because TanStack resets expanded={} on every
        // grouping change.
        autoResetExpanded: false,
        getExpandedRowModel: getExpandedRowModel(),
        getGroupedRowModel: getGroupedRowModelLightdash(),
        getCoreRowModel: getCoreRowModel(),
        meta: {
            columnProperties,
            minMaxMap,
        },
    });

    const { rows } = table.getRowModel();

    const rowVirtualizer = useVirtualizer({
        getScrollElement: () => containerRef.current,
        count: rows.length,
        estimateSize: () => CELL_HEIGHT,
        overscan: 25,
    });
    const virtualRows = rowVirtualizer.getVirtualItems();

    // Grouping-only mode renders a flat row model and visually merges repeated
    // row-index dimension values with rowSpan instead of TanStack group rows.
    const rowSpanMergesByColumnId = useMemo(() => {
        if (!groupingOnlyMode) return null;
        const groupedColumnIds = getGroupedDimColumnIds(
            data.indexValueTypes,
            columnOrder,
        );
        if (groupedColumnIds.length === 0) return null;
        return getRowSpanMerges(
            rows.length,
            groupedColumnIds,
            (rowIndex, columnId) => {
                const cell = rows[rowIndex]?.getValue(columnId) as
                    | { value?: ResultValue }
                    | undefined;
                return cell?.value?.raw;
            },
        );
    }, [groupingOnlyMode, data.indexValueTypes, columnOrder, rows]);

    // In merge mode, render every row (no virtualization) so rowSpans stay
    // contiguous — a block's first row must never be unmounted while its
    // absorbed siblings are visible.
    const renderedBodyRows = groupingOnlyMode
        ? rows.map((row, rowIndex) => ({ row, rowIndex }))
        : virtualRows.map((virtualRow) => ({
              row: rows[virtualRow.index],
              rowIndex: virtualRow.index,
          }));

    const getColumnTotalValueFromAxis = useCallback(
        (total: unknown, colIndex: number): ResultValue | null => {
            const value = last(data.headerValues)?.[colIndex];
            if (!value || !value.fieldId) throw new Error('Invalid pivot data');

            const item = getField(value.fieldId);
            // Column totals are warehouse-computed for every metric type, so
            // there is no `isSummable` gate; a null total just renders blank.
            if (total === null || total === undefined) {
                return null;
            }
            const formattedValue = formatItemValue(
                item,
                total,
                false,
                parameters,
            );

            return {
                raw: total,
                formatted: formattedValue,
            };
        },
        [data.headerValues, getField, parameters],
    );

    const getMetricAsRowColumnTotalValueFromAxis = useCallback(
        (total: unknown, rowIndex: number): ResultValue | null => {
            const value = last(data.columnTotalFields?.[rowIndex]);
            if (!value || !value.fieldId) throw new Error('Invalid pivot data');

            const item = getField(value.fieldId);
            if (total === null || total === undefined) {
                return null;
            }

            const formattedValue = formatItemValue(
                item,
                total,
                false,
                parameters,
            );

            return {
                raw: total,
                formatted: formattedValue,
            };
        },
        [data.columnTotalFields, getField, parameters],
    );

    const getUnderlyingFieldValues = useCallback(
        (rowIndex: number, colIndex: number) => {
            const visibleCells = rows[rowIndex].getVisibleCells();
            const visibleCell = visibleCells[colIndex];
            const item = visibleCell.column.columnDef.meta?.item;
            const fullItemValue = visibleCell.getValue() as ResultRow[0];
            const itemValue = fullItemValue.value;
            let underlyingValues =
                isField(item) && itemValue
                    ? { [getItemId(item)]: itemValue }
                    : {};
            visibleCells.forEach((cell, cellIndex) => {
                if (cell.column.columnDef.meta?.type === 'indexValue') {
                    if (cell.column.columnDef.id) {
                        const fullValue = cell.getValue() as
                            | ResultRow[0]
                            | undefined;

                        if (fullValue) {
                            underlyingValues[cell.column.columnDef.id] =
                                fullValue.value;
                        }
                    }
                } else if (cell.column.columnDef.meta?.type === 'label') {
                    const info = data.indexValues[rowIndex].find(
                        (indexValue) => indexValue.type === 'label',
                    );
                    if (info) underlyingValues[info.fieldId] = itemValue;
                } else if (
                    colIndex === cellIndex &&
                    cell.column.columnDef.meta?.headerInfo
                ) {
                    underlyingValues = {
                        ...underlyingValues,
                        ...cell.column.columnDef.meta.headerInfo,
                    };
                }
            });
            return underlyingValues;
        },
        [rows, data.indexValues],
    );

    // Find the data column index from headerInfo by matching against headerValues
    // Used for metricsAsRows mode to look up values from dataValues
    const findDataColumnIndex = useCallback(
        (
            headerInfo: Record<string, ResultValue> | undefined,
        ): number | undefined => {
            if (!headerInfo || data.headerValues.length === 0) return undefined;

            const numColumns = data.headerValues[0]?.length ?? 0;

            // Find column where ALL header dimension values match
            for (let colIdx = 0; colIdx < numColumns; colIdx++) {
                const allMatch = data.headerValues.every((headerRow) => {
                    const headerCell = headerRow[colIdx];
                    if (headerCell?.type !== 'value') return true;
                    const expectedValue = headerInfo[headerCell.fieldId]?.raw;
                    return headerCell.value?.raw === expectedValue;
                });
                if (allMatch) return colIdx;
            }
            return undefined;
        },
        [data.headerValues],
    );

    // Build rowFields entries from headerInfo (pivoted dimension values)
    // This ensures conditional formatting rules that reference pivoted dimensions work correctly
    const buildRowFieldsFromHeaderInfo = useCallback(
        (
            headerInfo: Record<string, ResultValue> | undefined,
        ): ConditionalFormattingRowFields => {
            if (!headerInfo) return {};

            return Object.entries(
                headerInfo,
            ).reduce<ConditionalFormattingRowFields>(
                (acc, [fieldId, resultValue]) => {
                    const field = getField(fieldId);
                    if (field && isDimension(field)) {
                        acc[fieldId] = {
                            field,
                            value: resultValue?.raw,
                        };
                    }
                    return acc;
                },
                {},
            );
        },
        [getField],
    );

    // Build rowFields for metricsAsRows mode by looking up metric values across rows
    // that share the same index dimension values (same "row group" in the original data)
    const buildRowFieldsForMetricsAsRows = useCallback(
        (
            rowIndex: number,
            headerInfo: Record<string, ResultValue> | undefined,
        ): ConditionalFormattingRowFields => {
            const dataColIndex = findDataColumnIndex(headerInfo);
            if (dataColIndex === undefined) return {};

            // Include pivoted dimension values from headerInfo
            const pivotedDimensionFields =
                buildRowFieldsFromHeaderInfo(headerInfo);

            const currentIndexDims = extractIndexDimensions(
                data.indexValues[rowIndex] ?? [],
            );

            // Include row dimension values from indexValues
            const rowDimensionFields =
                currentIndexDims.reduce<ConditionalFormattingRowFields>(
                    (acc, dim) => {
                        const field = getField(dim.fieldId);
                        if (field && isDimension(field)) {
                            acc[dim.fieldId] = { field, value: dim.value };
                        }
                        return acc;
                    },
                    {},
                );

            const metricFields =
                data.indexValues.reduce<ConditionalFormattingRowFields>(
                    (acc, indexValueRow, metricRowIdx) => {
                        const rowIndexDims =
                            extractIndexDimensions(indexValueRow);

                        // Only include metrics from same index dimension group
                        const sameIndexGroup =
                            currentIndexDims.length === rowIndexDims.length &&
                            currentIndexDims.every(
                                (dim, i) =>
                                    rowIndexDims[i]?.fieldId === dim.fieldId &&
                                    rowIndexDims[i]?.value === dim.value,
                            );
                        if (!sameIndexGroup) return acc;

                        const labelEntry = indexValueRow.find(
                            (v) => v.type === 'label',
                        );
                        if (!labelEntry) return acc;

                        const metricId = labelEntry.fieldId;
                        const field = getField(metricId);
                        const metricValue =
                            data.dataValues[metricRowIdx]?.[dataColIndex];

                        if (field && !isDimension(field)) {
                            acc[metricId] = { field, value: metricValue?.raw };
                        }
                        return acc;
                    },
                    {},
                );

            // Merge pivoted dimensions, row dimensions, and metrics
            return {
                ...pivotedDimensionFields,
                ...rowDimensionFields,
                ...metricFields,
            };
        },
        [
            data.indexValues,
            data.dataValues,
            findDataColumnIndex,
            getField,
            buildRowFieldsFromHeaderInfo,
        ],
    );

    // Build rowFields for normal pivot mode from visible cells with matching header context
    const buildRowFieldsFromVisibleCells = useCallback(
        (
            row: Row<ResultRow>,
            headerInfo: Record<string, ResultValue> | undefined,
        ): ConditionalFormattingRowFields => {
            // Include pivoted dimension values from headerInfo
            const pivotedDimensionFields =
                buildRowFieldsFromHeaderInfo(headerInfo);

            const cellFields = row
                .getVisibleCells()
                .reduce<ConditionalFormattingRowFields>((acc, c) => {
                    const cellMeta = c.column.columnDef.meta;
                    if (!cellMeta?.item) return acc;

                    // Include index (row) dimensions regardless of headerInfo match
                    // Include data cells only if they match the current pivot column context
                    const isIndexDimension = cellMeta.type === 'indexValue';
                    const matchesHeaderContext = isEqual(
                        cellMeta.headerInfo,
                        headerInfo,
                    );

                    if (isIndexDimension || matchesHeaderContext) {
                        const cellValue = c.getValue() as
                            | ResultRow[0]
                            | undefined;
                        acc[getItemId(cellMeta.item)] = {
                            field: cellMeta.item,
                            value: cellValue?.value?.raw,
                        };
                    }
                    return acc;
                }, {});

            // Merge pivoted dimensions with cell fields
            return { ...pivotedDimensionFields, ...cellFields };
        },
        [buildRowFieldsFromHeaderInfo],
    );

    const paddingTop = useMemo(() => {
        return virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
    }, [virtualRows]);

    const paddingBottom = useMemo(() => {
        return virtualRows.length > 0
            ? rowVirtualizer.getTotalSize() -
                  (virtualRows?.[virtualRows.length - 1]?.end || 0)
            : 0;
    }, [virtualRows, rowVirtualizer]);

    const cellsCountWithRowNumber = useMemo(() => {
        return (hideRowNumbers ? 0 : 1) + data.cellsCount;
    }, [hideRowNumbers, data.cellsCount]);

    useEffect(() => {
        // Grouping is driven by subtotals only. Row-grouping without subtotals
        // (groupingOnlyMode) now renders a FLAT row model with merged rowSpan
        // cells (see getRowSpanMerges / the body renderer below), so it must
        // NOT engage the TanStack grouping machinery.
        const groupingActive =
            showSubtotals || (showRowGrouping && !groupingOnlyMode);
        if (groupingActive) {
            const sortedColumns = getGroupedDimColumnIds(
                data.indexValueTypes,
                table.getState().columnOrder,
            );

            if (!isEqual(sortedColumns, table.getState().grouping)) {
                table.setGrouping(sortedColumns);
            }
        } else if (table.getState().grouping.length > 0) {
            table.resetGrouping();
        }
    }, [
        showSubtotals,
        showRowGrouping,
        groupingOnlyMode,
        data.indexValueTypes,
        table,
        columnOrder,
    ]);

    return (
        <Table
            miw={minTableWidth ? `max(100%, ${minTableWidth}px)` : '100%'}
            w={hasCustomWidths ? '100%' : undefined}
            className={
                hasCustomWidths
                    ? `${pivotStyles.fixedLayout}${className ? ` ${className}` : ''}`
                    : className
            }
            {...tableProps}
            containerRef={containerRef}
            isDashboard={isDashboard}
        >
            {hasCustomWidths && (
                <colgroup>
                    {colWidths.map((cw, i) => (
                        <col
                            key={i}
                            style={
                                cw !== undefined
                                    ? { width: `${cw}px` }
                                    : undefined
                            }
                        />
                    ))}
                </colgroup>
            )}
            <Table.Head withSticky>
                {data.headerValues.map((headerValues, headerRowIndex) => (
                    <Table.Row
                        key={`header-row-${headerRowIndex}`}
                        index={headerRowIndex}
                    >
                        {/* shows empty cell if row numbers are visible */}
                        {hideRowNumbers
                            ? null
                            : (() => {
                                  const rowNumberSticky =
                                      frozenLayout.size > 0
                                          ? {
                                                className: `${pivotStyles.stickyColumn} ${pivotStyles.stickyHeaderColumn}`,
                                                style: { left: 0 },
                                            }
                                          : ({} as {
                                                className?: string;
                                                style?: React.CSSProperties;
                                            });
                                  return headerRowIndex <
                                      data.headerValues.length - 1 ? (
                                      <Table.Cell
                                          className={rowNumberSticky.className}
                                          style={rowNumberSticky.style}
                                          isMinimal={isMinimal}
                                          w={rowNumberWidth}
                                          miw={rowNumberWidth}
                                          maw={rowNumberWidth}
                                      />
                                  ) : (
                                      <Table.CellHead
                                          className={rowNumberSticky.className}
                                          style={rowNumberSticky.style}
                                          isMinimal={isMinimal}
                                          w={rowNumberWidth}
                                          miw={rowNumberWidth}
                                          maw={rowNumberWidth}
                                          withBoldFont
                                      >
                                          #
                                      </Table.CellHead>
                                  );
                              })()}
                        {/* renders the title labels */}
                        {data.titleFields[headerRowIndex].map(
                            (titleField, titleFieldIndex) => {
                                const field = titleField?.fieldId
                                    ? getField(titleField?.fieldId)
                                    : undefined;

                                const isEmpty = !titleField?.fieldId;

                                const isHeaderTitle =
                                    titleField?.direction === 'header';

                                const isIndexTitle =
                                    titleField?.direction === 'index';

                                const isGroupByTitle =
                                    titleField?.direction === 'header';

                                const isLastHeaderRow =
                                    headerRowIndex ===
                                    data.headerValues.length - 1;

                                const titleMenuTarget:
                                    | PivotSortMenuTarget
                                    | undefined =
                                    isIndexTitle && titleField?.fieldId
                                        ? {
                                              kind: 'indexDim',
                                              reference: titleField.fieldId,
                                          }
                                        : isGroupByTitle && titleField?.fieldId
                                          ? {
                                                kind: 'groupByDim',
                                                reference: titleField.fieldId,
                                            }
                                          : undefined;
                                const titleIsClickable =
                                    !!titleMenuTarget && !!renderSortMenu;
                                const titleSortMatch =
                                    (isIndexTitle || isGroupByTitle) &&
                                    titleField?.fieldId
                                        ? sortBy?.find(
                                              (s) =>
                                                  s.fieldId ===
                                                      titleField.fieldId &&
                                                  !s.pivotValues?.length,
                                          )
                                        : undefined;
                                const titleSortField = titleSortMatch
                                    ? getField(titleSortMatch.fieldId)
                                    : undefined;
                                const titleSortIcon =
                                    titleSortMatch && titleSortField
                                        ? getSortIcon(
                                              titleSortField,
                                              titleSortMatch.descending,
                                          )
                                        : undefined;

                                const titleInnerContent =
                                    titleField?.fieldId ? (
                                        titleSortIcon ? (
                                            <Group
                                                display="inline-flex"
                                                spacing={4}
                                                noWrap
                                                align="center"
                                            >
                                                {getFieldLabel(
                                                    titleField.fieldId,
                                                )}
                                                <MantineIcon
                                                    icon={titleSortIcon}
                                                    size={14}
                                                />
                                            </Group>
                                        ) : (
                                            getFieldLabel(titleField.fieldId)
                                        )
                                    ) : undefined;

                                const titleWidthKey = titleField?.fieldId;
                                const titleWidth = titleWidthKey
                                    ? columnProperties[titleWidthKey]?.width
                                    : undefined;
                                const canResizeTitle =
                                    isLastHeaderRow &&
                                    onColumnWidthChange &&
                                    !isMinimal &&
                                    titleWidthKey;

                                const titlePivotCol =
                                    data.retrofitData.pivotColumnInfo[
                                        titleFieldIndex
                                    ];
                                const titleStickyProps = titlePivotCol
                                    ? getStickyHeaderProps(
                                          frozenLayout.get(
                                              titlePivotCol.fieldId,
                                          ),
                                      )
                                    : {};

                                return isEmpty ? (
                                    <Table.Cell
                                        key={`title-${headerRowIndex}-${titleFieldIndex}`}
                                        className={titleStickyProps.className}
                                        style={titleStickyProps.style}
                                        isMinimal={isMinimal}
                                        withMinimalWidth={
                                            !hasCustomWidths && !titleWidth
                                        }
                                    />
                                ) : (
                                    <Table.CellHead
                                        key={`title-${headerRowIndex}-${titleFieldIndex}`}
                                        className={
                                            [
                                                titleStickyProps.className,
                                                titleIsClickable
                                                    ? pivotStyles.clickableHeader
                                                    : undefined,
                                            ]
                                                .filter(Boolean)
                                                .join(' ') || undefined
                                        }
                                        style={titleStickyProps.style}
                                        withAlignRight={isHeaderTitle}
                                        isMinimal={isMinimal}
                                        withMinimalWidth={
                                            !hasCustomWidths &&
                                            (!isLastHeaderRow || !titleWidth)
                                        }
                                        withBoldFont
                                        withTooltip={
                                            isField(field)
                                                ? field.description
                                                : undefined
                                        }
                                        w={
                                            isLastHeaderRow
                                                ? titleWidth
                                                : undefined
                                        }
                                        miw={
                                            isLastHeaderRow
                                                ? titleWidth
                                                : undefined
                                        }
                                        maw={
                                            isLastHeaderRow
                                                ? titleWidth
                                                : undefined
                                        }
                                    >
                                        {titleIsClickable &&
                                        titleField &&
                                        titleMenuTarget &&
                                        renderSortMenu ? (
                                            <Menu
                                                shadow="md"
                                                position="bottom-start"
                                                withinPortal
                                            >
                                                <Menu.Target>
                                                    <Box
                                                        component="span"
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        {titleInnerContent}
                                                    </Box>
                                                </Menu.Target>
                                                <Menu.Dropdown>
                                                    {renderSortMenu(
                                                        titleMenuTarget,
                                                    )}
                                                </Menu.Dropdown>
                                            </Menu>
                                        ) : (
                                            titleInnerContent
                                        )}
                                        {canResizeTitle && (
                                            <div
                                                className={
                                                    resizeHandleClassName
                                                }
                                                role="separator"
                                                aria-orientation="vertical"
                                                onMouseDown={(e) =>
                                                    handleResizeStart(
                                                        e,
                                                        titleWidthKey,
                                                    )
                                                }
                                            />
                                        )}
                                    </Table.CellHead>
                                );
                            },
                        )}
                        {/* renders the header values or labels */}
                        {headerValues.map((headerValue, headerColIndex) => {
                            const isLabel = headerValue.type === 'label';
                            const field = getField(headerValue.fieldId);

                            const description =
                                isLabel && isField(field)
                                    ? field.description
                                    : undefined;

                            const isLastHeaderRow =
                                headerRowIndex === data.headerValues.length - 1;

                            // Look up saved width for this data column
                            const colInfo =
                                data.retrofitData.pivotColumnInfo[
                                    numLabelCols + headerColIndex
                                ];
                            const widthKey =
                                colInfo?.underlyingId || colInfo?.baseId;
                            const colWidth = widthKey
                                ? columnProperties[widthKey]?.width
                                : undefined;

                            const canResize =
                                isLastHeaderRow &&
                                onColumnWidthChange &&
                                !isMinimal &&
                                widthKey;

                            // Apply width on the last header row, or on parent rows
                            // when the cell spans exactly 1 column (single metric)
                            const effectiveWidth =
                                isLastHeaderRow ||
                                (!isLabel && headerValue.colSpan === 1)
                                    ? colWidth
                                    : undefined;

                            const headerValueStickyProps = colInfo
                                ? getStickyHeaderProps(
                                      frozenLayout.get(colInfo.fieldId),
                                  )
                                : {};

                            const isMetricLabelRow =
                                metricLabelHeaderRowIndex >= 0 &&
                                headerRowIndex === metricLabelHeaderRowIndex;
                            const columnIdentity =
                                pivotColumnIdentities[headerColIndex];
                            const columnMetricRef =
                                columnIdentity?.metricFieldId;
                            const isClickableHeader =
                                isMetricLabelRow &&
                                isLabel &&
                                !!renderSortMenu &&
                                !!columnMetricRef;
                            const sortMatch =
                                isMetricLabelRow && isLabel
                                    ? sortMatchByDataColIndex.get(
                                          headerColIndex,
                                      )
                                    : undefined;
                            const sortMatchItem = sortMatch
                                ? getField(sortMatch.fieldId)
                                : undefined;
                            const sortIcon =
                                sortMatch && sortMatchItem
                                    ? getSortIcon(
                                          sortMatchItem,
                                          sortMatch.descending,
                                      )
                                    : undefined;

                            const cellClassName = [
                                headerValueStickyProps.className,
                                isClickableHeader
                                    ? pivotStyles.clickableHeader
                                    : undefined,
                            ]
                                .filter(Boolean)
                                .join(' ');

                            const headerInnerContent = sortIcon ? (
                                <Group
                                    display="inline-flex"
                                    spacing={4}
                                    noWrap
                                    align="center"
                                >
                                    {isLabel
                                        ? getFieldLabel(headerValue.fieldId)
                                        : formatCellContent(headerValue)}
                                    <MantineIcon icon={sortIcon} size={14} />
                                </Group>
                            ) : isLabel ? (
                                getFieldLabel(headerValue.fieldId)
                            ) : (
                                formatCellContent(headerValue)
                            );

                            const pivotMenuTarget: PivotSortMenuTarget | null =
                                columnMetricRef && columnIdentity
                                    ? {
                                          kind: 'pivotColumn',
                                          dataColIndex: headerColIndex,
                                          metricReference: columnMetricRef,
                                          pivotValues:
                                              columnIdentity.pivotValues.map(
                                                  (p) => ({
                                                      reference: p.reference,
                                                      value: p.rawValue,
                                                  }),
                                              ),
                                      }
                                    : null;

                            return isLabel || headerValue.colSpan > 0 ? (
                                <Table.CellHead
                                    key={`header-${headerRowIndex}-${headerColIndex}`}
                                    className={cellClassName || undefined}
                                    style={headerValueStickyProps.style}
                                    isMinimal={isMinimal}
                                    withBoldFont={isLabel}
                                    withTooltip={description}
                                    colSpan={
                                        isLabel
                                            ? undefined
                                            : headerValue.colSpan
                                    }
                                    w={effectiveWidth}
                                    miw={effectiveWidth}
                                    maw={effectiveWidth}
                                >
                                    {isClickableHeader && pivotMenuTarget ? (
                                        <Menu
                                            shadow="md"
                                            position="bottom-start"
                                            withinPortal
                                        >
                                            <Menu.Target>
                                                <Box
                                                    component="span"
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    {headerInnerContent}
                                                </Box>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                                {renderSortMenu?.(
                                                    pivotMenuTarget,
                                                )}
                                            </Menu.Dropdown>
                                        </Menu>
                                    ) : (
                                        headerInnerContent
                                    )}
                                    {canResize && (
                                        <div
                                            className={resizeHandleClassName}
                                            role="separator"
                                            aria-orientation="vertical"
                                            onMouseDown={(e) =>
                                                handleResizeStart(e, widthKey)
                                            }
                                        />
                                    )}
                                </Table.CellHead>
                            ) : null;
                        })}
                        {/* render the total label */}
                        {hasRowTotals
                            ? data.rowTotalFields?.[headerRowIndex].map(
                                  (totalLabel, headerColIndex) =>
                                      totalLabel ? (
                                          <Table.CellHead
                                              key={`header-total-${headerRowIndex}-${headerColIndex}`}
                                              isMinimal={isMinimal}
                                              withBoldFont
                                              withMinimalWidth={
                                                  !hasCustomWidths
                                              }
                                          >
                                              {totalLabel.fieldId
                                                  ? `Total ${getFieldLabel(
                                                        totalLabel.fieldId,
                                                    )}`
                                                  : `Total`}
                                          </Table.CellHead>
                                      ) : (
                                          <Table.Cell
                                              key={`header-total-${headerRowIndex}-${headerColIndex}`}
                                              isMinimal={isMinimal}
                                              withMinimalWidth={
                                                  !hasCustomWidths
                                              }
                                          />
                                      ),
                              )
                            : null}
                    </Table.Row>
                ))}
            </Table.Head>

            <Table.Body>
                {!groupingOnlyMode && paddingTop > 0 && (
                    <VirtualizedArea
                        cellCount={cellsCountWithRowNumber}
                        height={paddingTop}
                    />
                )}

                {renderedBodyRows.map(({ row, rowIndex }) => {
                    if (!row) return null;

                    const toggleExpander = row.getToggleExpandedHandler();

                    return (
                        <Table.Row
                            key={`row-${rowIndex}-${data.pivotConfig.metricsAsRows}`}
                            index={rowIndex}
                        >
                            {row.getVisibleCells().map((cell, colIndex) => {
                                // Measure body cell widths in the first row only.
                                // Column widths are uniform across rows (CSS table
                                // layout), so one measurement per column is enough.
                                const measureRef =
                                    rowIndex === 0
                                        ? measureCellRef(cell.column.id)
                                        : undefined;
                                if (cell.column.id === ROW_NUMBER_COLUMN_ID) {
                                    const rowNumberStickyBody =
                                        frozenLayout.size > 0
                                            ? {
                                                  className:
                                                      pivotStyles.stickyColumn,
                                                  style: { left: 0 },
                                              }
                                            : ({} as {
                                                  className?: string;
                                                  style?: React.CSSProperties;
                                              });
                                    return (
                                        <Table.Cell
                                            key={`row-number-${rowIndex}`}
                                            ref={measureRef}
                                            className={
                                                rowNumberStickyBody.className
                                            }
                                            style={rowNumberStickyBody.style}
                                            isMinimal={isMinimal}
                                            w={rowNumberWidth}
                                            miw={rowNumberWidth}
                                            maw={rowNumberWidth}
                                        >
                                            {/* Suppress the row number on
                                             * group-header rows when we're
                                             * in grouping-only mode — those
                                             * rows act as visual headers
                                             * for the merged dim value, not
                                             * data rows, so a counter on
                                             * them reads as a parallel
                                             * sequence interleaved with the
                                             * leaf-row counter. */}
                                            {groupingOnlyMode &&
                                            row.getIsGrouped()
                                                ? null
                                                : flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext(),
                                                  )}
                                        </Table.Cell>
                                    );
                                }

                                // Grouping-only mode: merge repeated row-index
                                // dimension values with rowSpan. The block's
                                // first row renders the value spanning the
                                // block; absorbed rows render no <td> so the
                                // span covers their position.
                                const rowSpanMerge =
                                    rowSpanMergesByColumnId?.get(
                                        cell.column.id,
                                    )?.[rowIndex];
                                if (
                                    rowSpanMerge &&
                                    !rowSpanMerge.isBlockStart
                                ) {
                                    return null;
                                }

                                const meta = cell.column.columnDef.meta;
                                const isRowTotal = meta?.type === 'rowTotal';
                                const isDataColumn =
                                    meta?.type !== 'indexValue' &&
                                    meta?.type !== 'label' &&
                                    !isRowTotal;
                                let item = meta?.item;

                                if (
                                    data.pivotConfig.metricsAsRows &&
                                    isDataColumn
                                ) {
                                    const metricLabelInfo = data.indexValues[
                                        rowIndex
                                    ]?.find(
                                        (indexValue) =>
                                            indexValue.type === 'label',
                                    );
                                    if (metricLabelInfo) {
                                        item = getField(
                                            metricLabelInfo.fieldId,
                                        );
                                    }
                                } else if (item && isDimension(item)) {
                                    const underlyingId = data.indexValues[
                                        rowIndex
                                    ]?.find(
                                        (indexValue) =>
                                            indexValue.type === 'label',
                                    )?.fieldId;
                                    item = underlyingId
                                        ? getField(underlyingId)
                                        : item;
                                }

                                const fullValue =
                                    cell.getValue() as ResultRow[0];
                                const value = fullValue?.value;

                                // In merge mode an empty pivot data cell renders
                                // blank instead of the `∅`/`-` placeholder. An
                                // empty cell still carries a value wrapper whose
                                // `raw` is null (formatted as `∅`), or no wrapper
                                // at all, so we key off `value?.raw`; a real
                                // `raw: 0`/`''` is kept.
                                const isBlankMergeDataCell =
                                    groupingOnlyMode &&
                                    isDataColumn &&
                                    value?.raw == null;

                                // Build rowFields for this cell's pivot context only
                                // This ensures field comparisons use values from the same pivot column
                                const currentHeaderInfo =
                                    cell.column.columnDef.meta?.headerInfo;

                                const rowFieldsForCell = data.pivotConfig
                                    .metricsAsRows
                                    ? buildRowFieldsForMetricsAsRows(
                                          rowIndex,
                                          currentHeaderInfo,
                                      )
                                    : buildRowFieldsFromVisibleCells(
                                          row,
                                          currentHeaderInfo,
                                      );

                                const conditionalFormattingConfig =
                                    getConditionalFormattingConfig({
                                        field: item,
                                        value: value?.raw,
                                        minMaxMap,
                                        conditionalFormattings,
                                        rowFields: rowFieldsForCell,
                                    });

                                const conditionalFormattingResult =
                                    getConditionalFormattingColor({
                                        field: item,
                                        value: value?.raw,
                                        config: conditionalFormattingConfig,
                                        minMaxMap,
                                        getColorFromRange: (
                                            val,
                                            colorRange,
                                            minMaxRange,
                                        ) => {
                                            const effectiveColorRange =
                                                colorScheme === 'dark'
                                                    ? transformColorsForDarkMode(
                                                          colorRange,
                                                      )
                                                    : colorRange;
                                            return getColorFromRange(
                                                val,
                                                effectiveColorRange,
                                                minMaxRange,
                                            );
                                        },
                                    });

                                const applyToText =
                                    conditionalFormattingResult?.applyTo ===
                                    ConditionalFormattingColorApplyTo.TEXT;

                                const conditionalFormatting = (() => {
                                    const tooltipContent =
                                        getConditionalFormattingDescription(
                                            item,
                                            conditionalFormattingConfig,
                                            rowFieldsForCell,
                                            getConditionalRuleLabelFromItem,
                                        );

                                    if (
                                        !conditionalFormattingResult ||
                                        !isHexCodeColor(
                                            conditionalFormattingResult.color,
                                        )
                                    ) {
                                        return undefined;
                                    }

                                    // When applying to text, set color directly without background
                                    // When applying to cell, set background and calculate readable text color
                                    return applyToText
                                        ? {
                                              tooltipContent,
                                              color: conditionalFormattingResult.color,
                                          }
                                        : {
                                              tooltipContent,
                                              color: readableColor(
                                                  conditionalFormattingResult.color,
                                              ),
                                              backgroundColor:
                                                  conditionalFormattingResult.color,
                                          };
                                })();

                                // Font color is set by conditionalFormatting above
                                const fontColor = conditionalFormatting?.color;

                                // Get field description for label cells (metric labels when metricsAsRows is enabled)
                                const labelFieldDescription = (() => {
                                    if (meta?.type !== 'label')
                                        return undefined;
                                    const labelInfo = data.indexValues[
                                        rowIndex
                                    ]?.find(
                                        (indexValue) =>
                                            indexValue.type === 'label',
                                    );
                                    if (!labelInfo) return undefined;
                                    const labelField = getField(
                                        labelInfo.fieldId,
                                    );
                                    return isField(labelField)
                                        ? labelField.description
                                        : undefined;
                                })();

                                const suppressContextMenu =
                                    (value === undefined ||
                                        cell.getIsPlaceholder()) &&
                                    !cell.getIsAggregated() &&
                                    !cell.getIsGrouped();
                                const allowInteractions = suppressContextMenu
                                    ? undefined
                                    : !!value?.formatted;

                                const cellWidth = meta?.width;

                                const stickyCellProps = getStickyCellProps(
                                    meta?.frozenLayout,
                                );

                                const TableCellComponent = isRowTotal
                                    ? Table.CellHead
                                    : Table.Cell;
                                return (
                                    <TableCellComponent
                                        key={`value-${rowIndex}-${colIndex}-${data.pivotConfig.metricsAsRows}`}
                                        ref={measureRef}
                                        rowSpan={
                                            rowSpanMerge &&
                                            rowSpanMerge.rowSpan > 1
                                                ? rowSpanMerge.rowSpan
                                                : undefined
                                        }
                                        className={
                                            rowSpanMerge
                                                ? `${pivotStyles.mergedDimCell}${
                                                      stickyCellProps.className
                                                          ? ` ${stickyCellProps.className}`
                                                          : ''
                                                  }`
                                                : stickyCellProps.className
                                        }
                                        style={stickyCellProps.style}
                                        isMinimal={isMinimal}
                                        withAlignRight={isNumericItem(item)}
                                        w={cellWidth}
                                        miw={cellWidth}
                                        maw={cellWidth}
                                        withColor={conditionalFormatting?.color}
                                        withBoldFont={meta?.type === 'label'}
                                        withBackground={
                                            conditionalFormatting?.backgroundColor
                                        }
                                        withTooltip={
                                            labelFieldDescription ||
                                            conditionalFormatting?.tooltipContent
                                        }
                                        withInteractions={allowInteractions}
                                        withValue={value?.formatted}
                                        withMenu={(
                                            {
                                                isOpen,
                                                onClose,
                                                onCopy,
                                            }: MenuCallbackProps,
                                            render: RenderCallback,
                                        ) => (
                                            <ValueCellMenu
                                                opened={isOpen}
                                                rowIndex={rowIndex}
                                                colIndex={colIndex}
                                                item={item}
                                                value={value}
                                                getUnderlyingFieldValues={
                                                    isRowTotal
                                                        ? undefined
                                                        : getUnderlyingFieldValues
                                                }
                                                onClose={onClose}
                                                onCopy={onCopy}
                                                isMinimal={isMinimal}
                                            >
                                                {render()}
                                            </ValueCellMenu>
                                        )}
                                    >
                                        {cell.getIsGrouped() ? (
                                            // Grouping-only mode (row dedup
                                            // without subtotals): suppress the
                                            // carat + group count chrome and
                                            // just render the dim value. The
                                            // expand-toggle would be a no-op
                                            // anyway since groups are forced
                                            // expanded in this mode.
                                            groupingOnlyMode ? (
                                                flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )
                                            ) : (
                                                <Group spacing="two" noWrap>
                                                    <Button
                                                        compact
                                                        size="xs"
                                                        variant="subtle"
                                                        styles={(theme) => ({
                                                            root: {
                                                                height: 'unset',
                                                                paddingLeft:
                                                                    theme
                                                                        .spacing
                                                                        .two,
                                                                paddingRight:
                                                                    theme
                                                                        .spacing
                                                                        .xxs,
                                                                fontFeatureSettings:
                                                                    "'tnum'",
                                                            },
                                                            leftIcon: {
                                                                marginRight: 0,
                                                            },
                                                        })}
                                                        onClick={(
                                                            e: React.MouseEvent<HTMLButtonElement>,
                                                        ) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            toggleExpander();
                                                        }}
                                                        leftIcon={
                                                            <MantineIcon
                                                                size={14}
                                                                icon={
                                                                    row.getIsExpanded()
                                                                        ? IconChevronDown
                                                                        : IconChevronRight
                                                                }
                                                            />
                                                        }
                                                        style={{
                                                            color:
                                                                fontColor ??
                                                                'inherit',
                                                        }}
                                                    >
                                                        ({countSubRows(row)})
                                                    </Button>
                                                    {flexRender(
                                                        cell.column.columnDef
                                                            .cell,
                                                        cell.getContext(),
                                                    )}
                                                </Group>
                                            )
                                        ) : cell.getIsAggregated() ? (
                                            // In grouping-only mode the
                                            // aggregate row stands in for a
                                            // dedup "header" — render the dim
                                            // cells via getIsGrouped() above,
                                            // but suppress the subtotal metric
                                            // values so the row looks like a
                                            // leaf row with the dim filled in.
                                            groupingOnlyMode ? null : (
                                                flexRender(
                                                    cell.column.columnDef
                                                        .aggregatedCell ??
                                                        cell.column.columnDef
                                                            .cell,
                                                    cell.getContext(),
                                                )
                                            )
                                        ) : cell.getIsPlaceholder() ||
                                          isBlankMergeDataCell ? null : (
                                            flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )
                                        )}
                                    </TableCellComponent>
                                );
                            })}
                        </Table.Row>
                    );
                })}

                {!groupingOnlyMode && paddingBottom > 0 && (
                    <VirtualizedArea
                        cellCount={cellsCountWithRowNumber}
                        height={paddingBottom}
                    />
                )}
            </Table.Body>

            {hasColumnTotals ? (
                <Table.Footer withSticky>
                    {data.columnTotals?.map((row, totalRowIndex) => {
                        const totalRowCount = data.columnTotals?.length ?? 1;
                        const stickyIndex = totalRowCount - 1 - totalRowIndex;
                        return (
                            <Table.Row
                                key={`column-total-${totalRowIndex}`}
                                index={stickyIndex}
                            >
                                {/* shows empty cell if row numbers are visible */}
                                {hideRowNumbers
                                    ? null
                                    : (() => {
                                          const footerRowNumberSticky =
                                              frozenLayout.size > 0
                                                  ? {
                                                        className:
                                                            pivotStyles.stickyColumn,
                                                        style: { left: 0 },
                                                    }
                                                  : ({} as {
                                                        className?: string;
                                                        style?: React.CSSProperties;
                                                    });
                                          return (
                                              <Table.Cell
                                                  className={
                                                      footerRowNumberSticky.className
                                                  }
                                                  style={
                                                      footerRowNumberSticky.style
                                                  }
                                                  w={rowNumberWidth}
                                                  miw={rowNumberWidth}
                                                  maw={rowNumberWidth}
                                              />
                                          );
                                      })()}

                                {/* render the total label */}
                                {data.columnTotalFields?.[totalRowIndex].map(
                                    (totalLabel, totalColIndex) => {
                                        const footerPivotCol =
                                            data.retrofitData.pivotColumnInfo[
                                                totalColIndex
                                            ];
                                        const footerStickyProps = footerPivotCol
                                            ? getStickyCellProps(
                                                  frozenLayout.get(
                                                      footerPivotCol.fieldId,
                                                  ),
                                              )
                                            : {};
                                        return totalLabel ? (
                                            <Table.CellHead
                                                key={`footer-total-${totalRowIndex}-${totalColIndex}`}
                                                className={
                                                    footerStickyProps.className
                                                }
                                                style={footerStickyProps.style}
                                                isMinimal={isMinimal}
                                                withAlignRight
                                                withBoldFont
                                            >
                                                {totalLabel.fieldId
                                                    ? `Total ${getFieldLabel(
                                                          totalLabel.fieldId,
                                                      )}`
                                                    : `Total`}
                                            </Table.CellHead>
                                        ) : (
                                            <Table.Cell
                                                key={`footer-total-${totalRowIndex}-${totalColIndex}`}
                                                className={
                                                    footerStickyProps.className
                                                }
                                                style={footerStickyProps.style}
                                                isMinimal={isMinimal}
                                            />
                                        );
                                    },
                                )}

                                {row.map((total, totalColIndex) => {
                                    const value = data.pivotConfig.metricsAsRows
                                        ? getMetricAsRowColumnTotalValueFromAxis(
                                              total,
                                              totalRowIndex,
                                          )
                                        : getColumnTotalValueFromAxis(
                                              total,
                                              totalColIndex,
                                          );
                                    return value ? (
                                        <Table.CellHead
                                            key={`column-total-${totalRowIndex}-${totalColIndex}`}
                                            withAlignRight
                                            isMinimal={isMinimal}
                                            withBoldFont
                                            withInteractions
                                            withValue={value.formatted}
                                            withMenu={(
                                                {
                                                    isOpen,
                                                    onClose,
                                                    onCopy,
                                                }: MenuCallbackProps,
                                                render: RenderCallback,
                                            ) => (
                                                <TotalCellMenu
                                                    opened={isOpen}
                                                    onClose={onClose}
                                                    onCopy={onCopy}
                                                >
                                                    {render()}
                                                </TotalCellMenu>
                                            )}
                                        >
                                            {value.formatted}
                                        </Table.CellHead>
                                    ) : (
                                        <Table.Cell
                                            key={`footer-total-${totalRowIndex}-${totalColIndex}`}
                                            isMinimal={isMinimal}
                                        />
                                    );
                                })}

                                {hasRowTotals
                                    ? data.rowTotalFields?.[0].map(
                                          (_, index) => (
                                              <Table.Cell
                                                  key={`footer-empty-${totalRowIndex}-${index}`}
                                                  isMinimal={isMinimal}
                                              />
                                          ),
                                      )
                                    : null}
                            </Table.Row>
                        );
                    })}
                </Table.Footer>
            ) : null}
        </Table>
    );
};

export default PivotTable;
